import { useEffect, useRef, useState } from 'react';

/**
 * Telas que se recuperam sozinhas (ADR-0032).
 *
 * A TV e o painel do corredor ficam o dia inteiro abertos, diante de gente que não
 * é técnica e não vai apertar F5. Quatro coisas podem deixá-los desatualizados ou
 * parados — publicação nova, erro inesperado, rede que caiu e voltou, dias de
 * memória acumulada — e cada uma tem uma camada aqui.
 *
 * ## A regra de ouro: nunca recarregar sem servidor
 *
 * Recarregar sem rede troca a página pela tela de erro **do navegador**, e ela não
 * tem código nenhum para tentar de novo: a TV ficaria nela até alguém ir até lá.
 * Por isso toda recarga passa por `recarregarComSeguranca`, que antes pergunta ao
 * servidor se ele responde — e, se não responde, simplesmente não recarrega agora.
 *
 * ## E nunca em laço
 *
 * Uma recarga que não resolve o problema voltaria a ser pedida no carregamento
 * seguinte. A trava guarda a hora da última recarga na sessão da aba e recusa outra
 * em menos de um minuto — a não ser que alguém tenha pedido explicitamente (o botão
 * "Recarregar" de Dispositivos).
 */

const CHAVE_ULTIMA_RECARGA = 'cqp:ultima-recarga';
const INTERVALO_MINIMO_MS = 60_000;
/** De quanto em quanto tempo uma recarga pendente tenta de novo. */
const NOVA_TENTATIVA_MS = 15_000;

/** O servidor responde agora? `/health` é leve e não exige sessão. */
export async function servidorResponde(): Promise<boolean> {
  try {
    const resposta = await fetch('/health', { cache: 'no-store' });
    return resposta.ok;
  } catch {
    return false;
  }
}

function ultimaRecarga(): number {
  try {
    return Number(sessionStorage.getItem(CHAVE_ULTIMA_RECARGA) ?? 0);
  } catch {
    return 0;
  }
}

function marcarRecarga(): void {
  try {
    sessionStorage.setItem(CHAVE_ULTIMA_RECARGA, String(Date.now()));
  } catch {
    // Sem armazenamento (modo privado, TV restrita): a trava de laço some, mas a
    // checagem do servidor continua valendo.
  }
}

/**
 * Recarrega a página **se** for seguro. Devolve `false` quando não recarregou —
 * servidor fora ou recarga recente demais —, para quem chamou tentar de novo depois.
 */
export async function recarregarComSeguranca(
  opcoes: { readonly forcar?: boolean } = {},
): Promise<boolean> {
  if (!(await servidorResponde())) return false;
  if (opcoes.forcar !== true && Date.now() - ultimaRecarga() < INTERVALO_MINIMO_MS) return false;
  marcarRecarga();
  window.location.reload();
  return true;
}

/**
 * Uma recarga que **espera o momento certo**.
 *
 * `pendente` diz que é preciso recarregar; `seguro` diz que agora não atrapalha
 * ninguém — na TV, nenhuma chamada no palco; no painel, ninguém mexendo nele.
 * Enquanto as duas coisas valem, tenta a cada 15 segundos (o servidor pode estar
 * voltando de uma publicação).
 */
export function useRecargaPendente(pendente: boolean, seguro: boolean): void {
  useEffect(() => {
    if (!pendente || !seguro) return undefined;

    let vivo = true;
    let agendada: ReturnType<typeof setTimeout> | undefined;
    const tentar = (): void => {
      void recarregarComSeguranca().then((recarregou) => {
        if (!recarregou && vivo) agendada = setTimeout(tentar, NOVA_TENTATIVA_MS);
      });
    };
    agendada = setTimeout(tentar, 0);

    return () => {
      vivo = false;
      clearTimeout(agendada);
    };
  }, [pendente, seguro]);
}

/**
 * Há versão nova no servidor?
 *
 * A versão da tela é a da configuração carregada **na abertura da página**; a do
 * servidor chega pelo canal em tempo real a cada reconexão — e toda publicação
 * reinicia o servidor, então a reconexão vem sozinha.
 */
export function useVersaoNova(versaoDoServidor: string | null, versaoDaTela: string): boolean {
  return versaoDoServidor !== null && versaoDoServidor !== versaoDaTela;
}

/**
 * Vigia de dados: a tela está recebendo dado novo?
 *
 * Canal em tempo real e conferência periódica podem os dois ficar presos depois de
 * uma queda de rede longa — a página "volta", mas não se atualiza mais. Passado o
 * `limiteMs` sem dado novo, pede recarga; e como toda recarga checa o servidor
 * antes, com a rede ainda fora ela simplesmente espera.
 *
 * **Só com a tela visível.** Em segundo plano o painel para de consultar de
 * propósito, e o dado envelhece sem nada de errado. Recarregar ali ainda zeraria a
 * lista de pacientes já vistos — e uma chegada durante a recarga não seria avisada.
 */
export function useDadosParados(ultimoDadoEm: number, limiteMs: number): boolean {
  const [parado, setParado] = useState(false);

  useEffect(() => {
    const conferir = (): void => {
      setParado(
        document.visibilityState === 'visible' &&
          ultimoDadoEm > 0 &&
          Date.now() - ultimoDadoEm > limiteMs,
      );
    };
    conferir();
    const timer = setInterval(conferir, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, [ultimoDadoEm, limiteMs]);

  return parado;
}

/** Hora da recarga preventiva: madrugada, com a clínica fechada. */
const HORA_DA_RECARGA_DIARIA = 4;

/**
 * Recarga preventiva, uma vez por dia.
 *
 * Tela aberta por dias acumula memória — o navegador de TV, principalmente. A
 * recarga às 4h zera isso num horário em que ninguém está olhando. Devolve se a
 * recarga está **pendente**; quem usa decide o momento seguro.
 */
export function useRecargaDiaria(): boolean {
  const [pendente, setPendente] = useState(false);
  const abertaEm = useRef(Date.now());

  useEffect(() => {
    const conferir = (): void => {
      const agora = new Date();
      // Só se a página estiver aberta há mais de uma hora: sem isto, a própria
      // recarga das 4h seria recarregada de novo pelos minutos seguintes.
      const antiga = Date.now() - abertaEm.current > 60 * 60_000;
      if (agora.getHours() === HORA_DA_RECARGA_DIARIA && antiga) setPendente(true);
    };
    const timer = setInterval(conferir, 5 * 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return pendente;
}

/**
 * Ninguém está mexendo no painel agora?
 *
 * Recarregar no meio de um cadastro apagaria o que a pessoa digitou. "Ocioso" é: sem
 * janela aberta, sem campo em foco e sem toque ou tecla nos últimos 30 segundos.
 */
export function useOcioso(): boolean {
  const ultimaInteracao = useRef(Date.now());
  const [ocioso, setOcioso] = useState(false);

  useEffect(() => {
    const marcar = (): void => {
      ultimaInteracao.current = Date.now();
      setOcioso(false);
    };
    const conferir = (): void => {
      const foco = document.activeElement;
      const digitando =
        foco instanceof HTMLInputElement ||
        foco instanceof HTMLTextAreaElement ||
        foco instanceof HTMLSelectElement;
      const janelaAberta = document.querySelector('[role="dialog"]') !== null;
      setOcioso(!digitando && !janelaAberta && Date.now() - ultimaInteracao.current > 30_000);
    };

    window.addEventListener('pointerdown', marcar, { passive: true });
    window.addEventListener('keydown', marcar);
    const timer = setInterval(conferir, 5_000);
    return () => {
      window.removeEventListener('pointerdown', marcar);
      window.removeEventListener('keydown', marcar);
      clearInterval(timer);
    };
  }, []);

  return ocioso;
}

/**
 * Pedaço do sistema que não existe mais depois de uma publicação.
 *
 * Uma tela aberta antes da publicação ainda aponta para os arquivos do build
 * anterior; ao abrir uma tela nova, pede um arquivo que já foi trocado, e o Vite
 * avisa com `vite:preloadError`. A saída recomendada é recarregar para pegar o build
 * novo — uma vez, e com o servidor respondendo.
 */
export function instalarRecuperacaoDeCarga(): void {
  window.addEventListener('vite:preloadError', (evento) => {
    evento.preventDefault();
    void recarregarComSeguranca();
  });
}
